/**
 * FormatStatusTable - Reusable component for displaying format-level planned vs delivered
 * Used in: Proof of Posting, Activation Cockpit, etc.
 */

import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertTriangle } from "lucide-react";

export interface FormatStatusItem {
    formatId: number;
    formatName: string;
    planned: number;
    delivered: number;
    discrepancyReason?: string | null;
}

interface FormatStatusTableProps {
    formats: FormatStatusItem[];
    /** Label for the delivered column - defaults to "Delivered" */
    deliveredLabel?: string;
    /** Whether to show the total row */
    showTotal?: boolean;
    /** Compact mode for smaller displays */
    compact?: boolean;
}

export function FormatStatusTable({ 
    formats, 
    deliveredLabel = "Delivered",
    showTotal = true,
    compact = false 
}: FormatStatusTableProps) {
    if (!formats || formats.length === 0) {
        return (
            <div className="text-sm text-muted-foreground italic py-2">
                No format data available
            </div>
        );
    }

    const totalPlanned = formats.reduce((sum, f) => sum + f.planned, 0);
    const totalDelivered = formats.reduce((sum, f) => sum + f.delivered, 0);
    const isComplete = totalDelivered >= totalPlanned;

    return (
        <div className={`bg-gray-50 rounded ${compact ? 'p-2' : 'p-3'}`}>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-muted-foreground">
                        <th className="text-left font-medium pb-1">Format</th>
                        <th className="text-right font-medium pb-1 w-16">Planned</th>
                        <th className="text-right font-medium pb-1 w-20">{deliveredLabel}</th>
                    </tr>
                </thead>
                <tbody>
                    {formats.map((fmt, idx) => {
                        const isFormatComplete = fmt.delivered >= fmt.planned;
                        const hasDiscrepancy = !!fmt.discrepancyReason;
                        return (
                            <tr key={fmt.formatId || idx} className="border-t border-gray-200">
                                <td className={`py-1 font-medium ${compact ? '' : 'pr-2'}`}>
                                    {fmt.formatName}
                                    {hasDiscrepancy && (
                                        <span className="ml-1 text-orange-500" title={fmt.discrepancyReason || ''}>
                                            <AlertTriangle className="h-3 w-3 inline" />
                                        </span>
                                    )}
                                </td>
                                <td className="py-1 text-right">
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                                        {fmt.planned}
                                    </Badge>
                                </td>
                                <td className="py-1 text-right">
                                    <Badge 
                                        variant="outline" 
                                        className={`text-xs px-1.5 ${
                                            isFormatComplete 
                                                ? "bg-green-50 text-green-700" 
                                                : "bg-orange-50 text-orange-700"
                                        }`}
                                    >
                                        {fmt.delivered}
                                    </Badge>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                {showTotal && formats.length > 1 && (
                    <tfoot>
                        <tr className="border-t-2 border-gray-300 font-semibold">
                            <td className="pt-1">Total</td>
                            <td className="pt-1 text-right">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                                    {totalPlanned}
                                </Badge>
                            </td>
                            <td className="pt-1 text-right">
                                <Badge 
                                    variant="outline" 
                                    className={`text-xs px-1.5 ${
                                        isComplete 
                                            ? "bg-green-50 text-green-700" 
                                            : "bg-orange-50 text-orange-700"
                                    }`}
                                >
                                    {totalDelivered}
                                    {isComplete && <CheckCircle className="h-3 w-3 inline ml-1" />}
                                </Badge>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
